// src/components/dashboard/MasterDataPreview.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, Loader, AlertCircle } from 'lucide-react';

const MasterDataPreview = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [masterData, setMasterData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  // API Base URL
  const API_BASE_URL = 'https://dpp-api.acespireconsulting.com/api';

  // UPDATED - Fetch master data from product-groups endpoint
  const fetchMasterData = async () => {
    try {
      setLoading(true);
      setError(null);

      // NEW - Call product-groups endpoint with limit of 3
      const response = await fetch(`${API_BASE_URL}/product-groups?limit=3&offset=0`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product groups: ${response.status}`);
      }

      const data = await response.json();
      
      console.log('Master Data Preview API Response:', data);
      
      // NEW - Extract groups and total from new API format
      let groupsArray = [];
      let total = 0;
      
      if (data.success && data.data && data.data.groups) {
        groupsArray = data.data.groups;
        total = data.data.pagination?.total || 0;
      } else if (data.groups) {
        groupsArray = data.groups;
        total = data.pagination?.total || 0;
      }
      
      setTotalCount(total);
      setMasterData(groupsArray);
      
    } catch (err) {
      console.error('Error fetching master data:', err);
      
      let errorMessage = err.message;
      
      if (err.message.includes('Failed to fetch') || err.message.includes('Network')) {
        errorMessage = 'Network error. Please check your internet connection.';
      } else if (err.message.includes('CORS')) {
        errorMessage = 'CORS error. The API server may need to allow requests from this domain.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchMasterData();
  }, []);

  //  UPDATED - Navigate to product data page with size filter
  const handleViewProducts = (size) => {
    navigate(`/product-data?size=${size}`);
  };

  // Navigate to full master data page
  const handleViewTable = () => {
    navigate('/master-data');
  };

  // Loading State
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800">Product Groups</h3>
        </div>
        <div className="flex items-center justify-center py-12">
          <Loader className="w-8 h-8 text-blue-600 animate-spin" />
        </div>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800">Product Groups</h3>
        </div>
        <div className="flex flex-col items-center justify-center py-12">
          <AlertCircle className="w-12 h-12 text-red-500 mb-3" />
          <p className="text-sm text-red-600 text-center">{error}</p>
          <button
            onClick={fetchMasterData}
            className="mt-4 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Empty State - No Data
  if (totalCount === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800">Product Groups</h3>
          <button
            onClick={handleViewTable}
            className="px-4 py-2 text-blue-600 rounded-lg text-sm font-medium hover:bg-blue-50 transition-colors flex items-center gap-2 opacity-50 cursor-not-allowed"
            disabled
          >
            View Table
            <img src="/arrow.png" alt="Arrow Right" className="w-8 h-8" />
          </button>
        </div>
        <div className="text-center py-12">
          <div className="text-gray-400 mb-3">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
          </div>
          <p className="text-gray-600 text-sm">No master data available</p>
        </div>
      </div>
    );
  }

  // Data Available - Show Table (latest 3 groups)
  return (
    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
      {/* Header -  UPDATED to show count */}
      <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-800">
          Product Groups {totalCount > 3 && <span className="text-sm text-gray-500 font-normal">(Showing 3 of {totalCount})</span>}
        </h3>
        <button
          onClick={handleViewTable}
          className="px-4 py-2 text-blue-600 rounded-lg text-sm font-medium hover:bg-blue-50 transition-colors flex items-center gap-2"
        >
          View Table
          <img src="/arrow.png" alt="Arrow Right" className="w-8 h-8" />
        </button>
      </div>

      {/* Desktop Table - UPDATED to match MasterData columns */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead style={{ backgroundColor: '#D6E1F9' }}>
            <tr>
              {/* <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Product Img</th> */}
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Product Group</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Brand</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Category</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Products Count</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Products</th>
            </tr>
          </thead>
          <tbody>
            {masterData.map((master, index) => (
              <tr key={master.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                {/* Product Img */}
                {/* <td className="px-4 py-3">
                  <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center border border-gray-300">
                    <svg className="w-8 h-8 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <rect x="2" y="3" width="20" height="14" rx="2" ry="2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <line x1="8" y1="21" x2="16" y2="21" strokeWidth="2" strokeLinecap="round"/>
                      <line x1="12" y1="17" x2="12" y2="21" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </div>
                </td> */}
                {/* Product Name */}
                <td className="px-4 py-3 text-sm text-gray-700">{master.productName}</td>
                {/* Brand */}
                <td className="px-4 py-3 text-sm text-gray-700">{master.brand}</td>
                {/* Category */}
                <td className="px-4 py-3 text-sm text-gray-700">{master.category}</td>
                {/* Products Count */}
                <td className="px-4 py-3 text-sm text-gray-700">{master.productsCount}</td>
                {/* Products (Action) */}
                <td className="px-4 py-3">
                  <button 
                    onClick={() => handleViewProducts(master.size)}
                    className="px-4 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                  >
                    View Products
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile/Tablet View - Cards - UPDATED to match MasterData */}
      <div className="lg:hidden p-4 space-y-3">
        {masterData.map((master, index) => (
          <div key={master.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-16 h-16 bg-gray-100 rounded flex items-center justify-center border border-gray-300 flex-shrink-0">
                <svg className="w-10 h-10 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <rect x="2" y="3" width="20" height="14" rx="2" ry="2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <line x1="8" y1="21" x2="16" y2="21" strokeWidth="2" strokeLinecap="round"/>
                  <line x1="12" y1="17" x2="12" y2="21" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <div className="flex-1">
                <p className="text-xs text-gray-500 mb-1">Product Group</p>
                <p className="text-sm font-semibold text-gray-900">
                  {master.productName}
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <p className="text-xs text-gray-500">Brand</p>
                  <p className="text-sm text-gray-700">{master.brand}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Category</p>
                  <p className="text-sm text-gray-700">{master.category}</p>
                </div>
              </div>
              <div>
                <p className="text-xs text-gray-500">Products Count</p>
                <p className="text-sm text-gray-700">{master.productsCount} Products</p>
              </div>
              <div className="pt-2">
                <button
                  onClick={() => handleViewProducts(master.size)}
                  className="w-full px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 active:bg-blue-800 transition-colors flex items-center justify-center gap-2"
                >
                  <Eye className="w-4 h-4" />
                  View Products
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
 
    </div>
  );
};

export default MasterDataPreview;